"use client";

import {
  BetaCardBody,
  BetaCardRootContainer,
  SkeletonBlock,
  Text,
} from "@zapier/design-system";
import { useConnectionStats } from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import styles from "./ConnectionsStatTiles.module.css";

interface StatTileProperty {
  label: string;
  value: number | undefined;
  isLoading: boolean;
}

function StatTile({ label, value, isLoading }: StatTileProperty) {
  const isReady = !isLoading && value !== undefined;

  return (
    <BetaCardRootContainer
      className={styles.tile}
      variant="outlined"
      padding="small"
      aria-label={isReady ? `${label}: ${value}` : undefined}
    >
      <BetaCardBody className={styles.tileBody}>
        <Text type="SmallPrint3" color="TextWeakest">
          {label}
        </Text>
        {isReady ? (
          <Text type="SectionHeader" tag="p">
            {value}
          </Text>
        ) : (
          <div aria-hidden="true">
            <SkeletonBlock width={32} height={24} borderRadius="small" />
          </div>
        )}
      </BetaCardBody>
    </BetaCardRootContainer>
  );
}

export function ConnectionsStatTiles() {
  const t = useTranslations("connections-stat-tiles");
  const { personal, managed, total, isLoading, isError } = useConnectionStats();

  // A failed background fetch for this summary row shouldn't block the rest
  // of the page (the connections table has its own error handling); hide
  // the tiles instead of showing an alert over three small numbers.
  if (isError) {
    return null;
  }

  return (
    <div className={styles.row} aria-busy={isLoading}>
      <StatTile
        label={t("labels.personal")}
        value={personal}
        isLoading={isLoading}
      />
      <StatTile
        label={t("labels.managed")}
        value={managed}
        isLoading={isLoading}
      />
      <StatTile label={t("labels.total")} value={total} isLoading={isLoading} />
    </div>
  );
}
